/*
 * What a change makes stale (frontend performance plan, W5, F05 and F12): the mutation-to-read
 * matrix, in one place. A committed change reads again what it can have changed, for the
 * conversations it touched, and nothing else: marking one conversation seen no longer re-reads the
 * saved views, the search typeahead, the import progress or a send's status.
 *
 * | Change                        | Reads again                                                    |
 * |-------------------------------|----------------------------------------------------------------|
 * | seen, unseen                  | lists, counts, this workspace's badge, the touched threads     |
 * | done, trash, snooze, unsnooze | the same, and the reply agent's narrowed list                  |
 * | label, confirm, meeting mark  | lists, counts, the touched threads, the agent's narrowed list  |
 * | reply sent                    | lists, counts, badge, the thread, the agent's rows and plan    |
 * | arrival (new mail, an expiry) | lists, counts, badge, the open thread, the agent's rows/plan   |
 *
 * Never read again by any of them: saved views, suggestions, import progress, search coverage.
 */
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { agentListKey } from "../agent/agent-reads.ts";
import { inboxKey } from "../queries.ts";
import { inboxApi } from "./api.ts";

export type InboxChange =
  | "seen"
  | "done"
  | "trash"
  | "snooze"
  | "label"
  | "meeting"
  | "assign"
  | "sent"
  | "arrival";

type Read = "list" | "counts" | "needs-reply" | "thread" | "agent-list" | "agent";

const AFFECTED: Readonly<Record<InboxChange, readonly Read[]>> = {
  seen: ["list", "counts", "needs-reply", "thread"],
  done: ["list", "counts", "needs-reply", "thread", "agent-list"],
  trash: ["list", "counts", "needs-reply", "thread", "agent-list"],
  snooze: ["list", "counts", "needs-reply", "thread", "agent-list"],
  label: ["list", "counts", "needs-reply", "thread", "agent-list"],
  // A mark moves the thread between Meeting requests and Needs reply, and a yes can relabel it.
  meeting: ["list", "counts", "needs-reply", "thread", "agent-list"],
  assign: ["list", "counts", "needs-reply", "thread", "agent-list"],
  sent: ["list", "counts", "needs-reply", "thread", "agent-list", "agent"],
  arrival: ["list", "counts", "needs-reply", "thread", "agent-list", "agent"],
};

type Keys = (workspaceId: string, planIds: readonly string[] | null) => (readonly unknown[])[];

/** Each read's keys: a change names its conversations; an arrival may have touched any open one. */
const KEYS: Readonly<Record<Read, Keys>> = {
  list: (workspaceId) => [[...inboxKey(workspaceId), "list"]],
  counts: (workspaceId) => [[...inboxKey(workspaceId), "counts"]],
  "needs-reply": (workspaceId) => [[...inboxKey(workspaceId), "needs-reply"]],
  thread: (workspaceId, planIds) =>
    planIds === null
      ? [[...inboxKey(workspaceId), "thread"]]
      : planIds.map((id) => [...inboxKey(workspaceId), "thread", id]),
  "agent-list": (workspaceId) => [agentListKey(workspaceId)],
  agent: (workspaceId, planIds) => [
    ["agent", workspaceId],
    ...(planIds === null
      ? [["plan", workspaceId]]
      : planIds.map((id) => ["plan", workspaceId, id])),
  ],
};

/** The reads a change makes stale, as the keys to invalidate. */
function affectedKeys(
  workspaceId: string,
  change: InboxChange,
  planIds: readonly string[] | null,
): readonly (readonly unknown[])[] {
  return AFFECTED[change].flatMap((read) => KEYS[read](workspaceId, planIds));
}

/** Reads again what a committed change made stale; the screen does not wait for it. */
export function refreshAfter(
  client: QueryClient,
  workspaceId: string,
  change: InboxChange,
  planIds: readonly string[] | null,
): Promise<unknown> {
  return Promise.all(
    affectedKeys(workspaceId, change, planIds).map((queryKey) =>
      client.invalidateQueries({ queryKey: [...queryKey] }),
    ),
  );
}

/** How often an open inbox asks whether anything changed, and how far each ask is spread (ms). */
const ARRIVAL_MS = 20_000;
const ARRIVAL_JITTER_MS = 5_000;

/**
 * New mail, another rep's change, a seller's answer and a snooze coming due, picked up while the
 * inbox stays open and focused (W5, F06): the workspace's revision is asked every 20 to 25 s while
 * the tab is visible (hidden tabs pause, a reconnect asks at once), and the reads an arrival makes
 * stale are read again only when it moved. One small read per interval while nothing happens.
 */
export function useArrivals(workspaceId: string, enabled: boolean, random = Math.random): void {
  const client = useQueryClient();
  const revision = useQuery({
    queryKey: [...inboxKey(workspaceId), "revision"],
    enabled,
    queryFn: ({ signal }) => inboxApi.revision(workspaceId, signal),
    refetchInterval: () => ARRIVAL_MS + Math.round(random() * ARRIVAL_JITTER_MS),
    refetchOnReconnect: true,
  });
  const shown = useRef<string | null>(null);
  useEffect(() => {
    const now = revision.data;
    if (now === undefined) return;
    const before = shown.current;
    shown.current = now;
    if (before === null || before === now) return;
    void refreshAfter(client, workspaceId, "arrival", null);
  }, [client, workspaceId, revision.data]);
}
